import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

export const SERVICE_WORKER_UPDATE_EVENT = 'service-worker-update-available';

interface ServiceWorkerUpdateEventDetail {
  activateUpdate: () => void;
}

export type ServiceWorkerUpdateEvent = CustomEvent<ServiceWorkerUpdateEventDetail>;

interface ServiceWorkerUpdateContextValue {
  isUpdateAvailable: boolean;
  activateUpdate: () => void;
  dismissUpdate: () => void;
}

const ServiceWorkerUpdateContext = createContext<ServiceWorkerUpdateContextValue | null>(null);

export const notifyServiceWorkerUpdate = (activateUpdate: () => void) => {
  if (typeof window === 'undefined') {
    return;
  }

  const event: ServiceWorkerUpdateEvent = new CustomEvent(SERVICE_WORKER_UPDATE_EVENT, {
    detail: { activateUpdate },
  });

  window.dispatchEvent(event);
};

export const useServiceWorkerUpdate = (): ServiceWorkerUpdateContextValue => {
  const context = useContext(ServiceWorkerUpdateContext);

  if (!context) {
    throw new Error('useServiceWorkerUpdate must be used within a ServiceWorkerUpdateProvider');
  }

  return context;
};

export const ServiceWorkerUpdateProvider = ({ children }: { children: React.ReactNode }) => {
  const [isUpdateAvailable, setIsUpdateAvailable] = useState(false);
  const activateUpdateRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    const handleUpdateAvailable = (event: Event) => {
      const { detail } = event as ServiceWorkerUpdateEvent;

      activateUpdateRef.current = detail.activateUpdate;
      setIsUpdateAvailable(true);
    };

    window.addEventListener(SERVICE_WORKER_UPDATE_EVENT, handleUpdateAvailable);

    return () => {
      window.removeEventListener(SERVICE_WORKER_UPDATE_EVENT, handleUpdateAvailable);
    };
  }, []);

  const activateUpdate = useCallback(() => {
    if (activateUpdateRef.current) {
      activateUpdateRef.current();
      setIsUpdateAvailable(false);
    }
  }, []);

  const dismissUpdate = useCallback(() => {
    setIsUpdateAvailable(false);
  }, []);

  const value = useMemo(
    () => ({
      isUpdateAvailable,
      activateUpdate,
      dismissUpdate,
    }),
    [activateUpdate, dismissUpdate, isUpdateAvailable]
  );

  return <ServiceWorkerUpdateContext.Provider value={value}>{children}</ServiceWorkerUpdateContext.Provider>;
};